import { useCallback, useEffect, useRef, useState } from "react";

import { useAppActive } from "./useAppActive";
import {
  elapsedSince,
  nextCueAt,
  readClock,
  remainingUntil,
  startedAtFor,
  useClockOf,
  useWakeAt,
  type TimerClock,
  type TimerMode,
} from "./timerClock";

// Generic deadline/elapsed timer engine (lifted from apps/mobile into
// @heraion/ui in Fase 2 so mobile and the future web-rnw logger share ONE
// engine, next/consolidation.md; the legacy apps/web copy went with RW-14).
// Two consumers wrap it:
//
//   - useRestTimer (count-DOWN): the rest between sets. Schedules a notifee
//     cue at the deadline so the bell rings even with the app backgrounded.
//   - the timed-exercise timer (count-UP or count-DOWN): planks and other
//     isometric holds logged in seconds (inbox 3dd20b1c).
//
// Antenore's call (2026-06-05): "rendi e generalizza il codice del rest timer,
// così che si possano impostare countdown e timer default per esercizio e
// globali, come il rest timer." So the engine is mode-agnostic; the
// rest-specific bits (default-resolution chain, exercise slug, notifee channel
// naming) live in the thin adapters, not here.
//
// No direct react-native import: the one platform read is useAppActive, whose
// web twin is always true, so it runs unchanged on web. The clock and its one
// reading live in ./timerClock. The notifee scheduling stays a caller concern
// (onSchedule/onCancel) — the engine never imports the notification layer.
//
// #326: the engine does NOT re-render with time. It wakes on the cue instants
// alone (-3, -2, -1 s and zero), foreground or not, so the logger that hosts
// it stays still while a rest runs; the number on screen is useTimerDisplay's,
// in the one component that shows it.

export type { TimerMode } from "./timerClock";
export { LEAD_IN_SEC } from "./timerClock";

// The clock (mode, endsAt, startedAt, totalSec, the paused captures) plus what
// the LOGIC reads off it. ⛔ No continuous value lives here any more (#326):
// the engine re-reads the clock only at the cue instants (-3, -2, -1 s, zero)
// and on every action, so a second-by-second number read from this state
// would be stale between them. A readout uses useTimerDisplay; a value taken
// at a tap uses readElapsedSec.
export type CountTimerState = TimerClock & {
  // down: whole seconds left as of the last cue instant or action. Exact on
  // each of -3/-2/-1/0, which is all usePhaseCues asks of it. up: 0.
  remainingSec: number;
  // The deadline has arrived and the clock is not held at it by a pause
  // (readClock owns the definition).
  atZero: boolean;
  isPaused: boolean;
  isRunning: boolean;
  // Opaque caller tag (the rest timer stores the exercise slug here so the
  // in-banner ⚙ knows which catalog row to PATCH). Null while idle.
  tag: string | null;
};

export type StartOptions = {
  seconds: number;
  mode?: TimerMode;
  tag?: string;
  // OPT-IN, count-down only (TM-13). A plain countdown measures at most the
  // number it started from: elapsed is derived as totalSec - remainingSec and
  // remaining floors at 0, so an athlete who holds 45s on a 20s countdown can
  // only ever be credited 20 ("il countdown non misura, conta alla rovescia
  // una prescrizione" — inbox 08ec8624). With this flag the countdown also
  // keeps a start instant, so elapsedSec is the REAL time held and goes on
  // past the deadline. ⛔ Left off by useRestTimer on purpose: for rest, zero
  // means finished (useRestTimer reads atZero as "done"),
  // and nothing about that path changes.
  overtime?: boolean;
};

export type CountTimerHooks = {
  // Called whenever a wall-clock cue should fire at `deadlineMs`: the down
  // deadline, or an up-to-target end (when started with seconds > 0). The
  // adapter wires this to notifee. Open-ended stopwatches never call it.
  onSchedule?: (deadlineMs: number) => void;
  // Called whenever a scheduled cue must be cancelled (reset/pause/restart).
  onCancel?: () => void;
};

export type UseCountTimer = {
  state: CountTimerState;
  /** The seconds held, read off the wall clock at the moment of the call
   *  (TM-13's recorded value). Never stale: it does not wait for a render. */
  readElapsedSec: () => number;
  start: (opts: StartOptions) => void;
  add: (seconds: number) => void;
  reset: () => void;
  pause: () => void;
  resume: () => void;
};

export function useCountTimer(hooks: CountTimerHooks = {}): UseCountTimer {
  const [mode, setMode] = useState<TimerMode>("down");
  const [endsAt, setEndsAt] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [totalSec, setTotalSec] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  // down: captured remaining; up: captured elapsed. Non-null === paused.
  const [pausedSec, setPausedSec] = useState<number | null>(null);
  // Overtime countdowns run TWO clocks (a deadline for the cue, a start
  // instant for the measure), so pausing one has to capture both: pausedSec
  // alone would freeze the remaining and silently drop the seconds already
  // held. Null for every non-overtime timer, which is why the rest timer's
  // pause path is untouched.
  const [pausedElapsedSec, setPausedElapsedSec] = useState<number | null>(null);
  const [tag, setTag] = useState<string | null>(null);

  // Keep the latest callbacks in a ref so re-arming the deadline doesn't have
  // to list them as deps (the adapter passes fresh closures each render).
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;

  const isRunning = endsAt !== null || startedAt !== null;
  const runningRef = useRef(isRunning);
  runningRef.current = isRunning;

  // Back in the foreground: re-read the clock at once rather than wait for a
  // wake the OS may have held back.
  const active = useAppActive();
  useEffect(() => {
    if (active && runningRef.current) setNow(Date.now());
  }, [active]);

  // One timeout per cue instant, and none in between: cues and atZero fire on
  // time, and nothing else here moves with the clock (#326).
  useWakeAt(endsAt === null ? null : nextCueAt(endsAt, now), setNow);

  // Unmounting a running timer takes its scheduled cue with it: the count is
  // gone, and a trigger left behind rings with nothing on screen to explain it.
  useEffect(
    () => () => {
      if (runningRef.current) hooksRef.current.onCancel?.();
    },
    [],
  );

  const clock = useClockOf({
    mode,
    endsAt,
    startedAt,
    totalSec,
    pausedSec,
    pausedElapsedSec,
  });
  const { remainingSec, atZero } = readClock(clock, now);
  // A tap reads the clock as it is now, through a ref, so the value it records
  // never depends on when this hook last rendered.
  const clockRef = useRef(clock);
  clockRef.current = clock;
  const readElapsedSec = useCallback(
    () => readClock(clockRef.current, Date.now()).elapsedSec,
    [],
  );

  const start = useCallback((opts: StartOptions) => {
    const nextMode = opts.mode ?? "down";
    const seconds = opts.seconds;
    if (!Number.isFinite(seconds) || seconds < 0) return;
    // Down needs a positive duration; up accepts 0 (open-ended stopwatch).
    if (nextMode === "down" && seconds <= 0) return;
    const nowMs = Date.now();
    setMode(nextMode);
    setTotalSec(seconds);
    setPausedSec(null);
    setPausedElapsedSec(null);
    setTag(opts.tag ?? null);
    setNow(nowMs);
    if (nextMode === "down") {
      const target = nowMs + seconds * 1000;
      setEndsAt(target);
      // The second clock an overtime countdown measures from.
      setStartedAt(opts.overtime === true ? nowMs : null);
      hooksRef.current.onSchedule?.(target);
    } else {
      setStartedAt(nowMs);
      setEndsAt(null);
      // Count-up to a target arms a cue at the target; open-ended does not.
      if (seconds > 0) hooksRef.current.onSchedule?.(nowMs + seconds * 1000);
      else hooksRef.current.onCancel?.();
    }
  }, []);

  // ±N seconds. Down: shift the deadline (clamped to now so "+60" past zero is
  // a fresh 60s, never a notifee fired early). Up: nudge the target. Both keep
  // a paused timer's captured value in sync. The clock is re-read here: no
  // interval would pick the moved deadline up any more (#326).
  const add = useCallback((seconds: number) => {
    if (!Number.isFinite(seconds) || seconds === 0) return;
    const nowMs = Date.now();
    setNow(nowMs);
    setPausedSec((prev) => (prev !== null ? Math.max(0, prev + seconds) : prev));
    setTotalSec((prev) => Math.max(0, prev + seconds));
    setEndsAt((prev) => {
      if (prev === null) return prev;
      const base = Math.max(nowMs, prev);
      const next = Math.max(nowMs, base + seconds * 1000);
      hooksRef.current.onSchedule?.(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setEndsAt(null);
    setStartedAt(null);
    setTotalSec(0);
    setPausedSec(null);
    setPausedElapsedSec(null);
    setTag(null);
    hooksRef.current.onCancel?.();
  }, []);

  const pause = useCallback(() => {
    if (endsAt !== null) {
      setPausedSec(remainingUntil(endsAt, Date.now()));
      setEndsAt(null);
      // Overtime countdown: capture the held time too, or resuming would
      // restart the measure from zero.
      if (startedAt !== null) {
        setPausedElapsedSec(elapsedSince(startedAt, Date.now()));
        setStartedAt(null);
      }
      hooksRef.current.onCancel?.();
      return;
    }
    if (startedAt !== null) {
      setPausedSec(elapsedSince(startedAt, Date.now()));
      setStartedAt(null);
      hooksRef.current.onCancel?.();
    }
  }, [endsAt, startedAt]);

  const resume = useCallback(() => {
    if (pausedSec === null) return;
    const nowMs = Date.now();
    if (mode === "down") {
      // A plain countdown paused at 0 has nothing left to run. An overtime
      // one does: it was paused PAST the deadline, so it resumes counting up.
      if (pausedSec <= 0 && pausedElapsedSec === null) {
        setPausedSec(null);
        return;
      }
      // Past the deadline the target is now: remaining stays 0 and no second
      // cue is armed, the bell already rang.
      const target = pausedSec > 0 ? nowMs + pausedSec * 1000 : nowMs;
      setEndsAt(target);
      setNow(nowMs);
      setPausedSec(null);
      if (pausedElapsedSec !== null) {
        setStartedAt(startedAtFor(nowMs, pausedElapsedSec));
        setPausedElapsedSec(null);
      }
      if (pausedSec > 0) hooksRef.current.onSchedule?.(target);
      return;
    }
    // up: rebuild startedAt so elapsed continues from the captured value.
    setStartedAt(startedAtFor(nowMs, pausedSec));
    setNow(nowMs);
    setPausedSec(null);
    if (totalSec > 0) {
      hooksRef.current.onSchedule?.(nowMs + Math.max(0, totalSec - pausedSec) * 1000);
    }
  }, [pausedSec, pausedElapsedSec, mode, totalSec]);

  return {
    state: {
      ...clock,
      remainingSec,
      atZero,
      isPaused: pausedSec !== null,
      isRunning,
      tag,
    },
    readElapsedSec,
    start,
    add,
    reset,
    pause,
    resume,
  };
}
